import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    useMemo,
} from 'react';
import { useSearchParams } from 'next/navigation';
import {
    updateFlowmapData,
    removeFlowmapLayers,
    calculateFlowmapBounds,
    LAYER_IDS,
} from './utils/flowmapLayerManager';
import { FLOWMAP_CONFIG } from './utils/constants';
import { createFlowmapPopup } from './utils/flowmapPopups';
import { getFlowmapChunk } from '../../api/flowmap';
import { applyFlowmapColors } from './utils/flowmapColors';

const FlowmapContext = createContext(null);

function buildFlowmapFilters(data) {
    const safeName = (iso) => {
        const upper = (iso || '').toUpperCase();
        if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
            try {
                const name = new Intl.DisplayNames(['en'], {
                    type: 'region',
                }).of(upper);
                if (name) return name;
            } catch {
                /* fallthrough */
            }
        }
        return upper;
    };

    const unique = (arr, key) => [
        ...new Set(arr.map((i) => i[key]).filter(Boolean)),
    ];
    const byLabel = (a, b) => String(a.label).localeCompare(String(b.label));

    const seenHsCodes = new Set();
    const hsCodes = [];
    data.forEach((item) => {
        if (item.hsCode && !seenHsCodes.has(item.hsCode)) {
            seenHsCodes.add(item.hsCode);
            hsCodes.push({
                id: item.hsCode,
                label: item.hsProductName || item.hsCode,
            });
        }
    });

    return {
        from_countries: unique(data, 'startCountryIso')
            .map((iso) => ({ id: iso, label: safeName(iso) }))
            .sort(byLabel),
        to_countries: unique(data, 'endCountryIso')
            .map((iso) => ({ id: iso, label: safeName(iso) }))
            .sort(byLabel),
        sources: unique(data, 'dataSource')
            .map((s) => ({ id: s, label: s }))
            .sort(byLabel),
        types: unique(data, 'dataType')
            .map((t) => ({ id: t, label: t }))
            .sort(byLabel),
        units: unique(data, 'unit')
            .map((u) => ({ id: u, label: u }))
            .sort(byLabel),
        hs_codes: hsCodes.sort(byLabel),
        years: unique(data, 'year')
            .map((y) => ({ id: y, label: y }))
            .sort(byLabel),
    };
}

export const FlowmapProvider = ({ children, data, showFlowmapLayer, map }) => {
    const searchParams = useSearchParams();
    const [activeFilterCount, setActiveFilterCount] = useState(0);
    const [flowmapData, setFlowmapData] = useState(data);
    const [filteredFlowmapData, setFilteredFlowmapData] = useState(data);
    const [resetFiltersFn, setResetFilters] = useState(() => () => {});
    const [isLoadingFlowmap, setIsLoadingFlowmap] = useState(false);
    const [flowmapError, setFlowmapError] = useState(null);

    const flowmapActiveByUrl = searchParams.get('flowmap') === 'true';
    const [zoomOnceOnData, setZoomOnceOnData] = useState(false);
    const [urlFiltersReady, setUrlFiltersReady] = useState(false);

    const [showFlowmap, setShowFlowmap] = useState(flowmapActiveByUrl);

    const isInitialLoad = useRef(true);
    const hasFlowmapBeenShown = useRef(false);
    const previousFilteredDataRef = useRef(null);
    const currentPopup = useRef(null);
    const persistentPopup = useRef(null);
    const initialFilteredLoaded = useRef(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional run-once: reads initial URL state
    useEffect(() => {
        const active =
            sessionStorage.getItem('showFlowmap') || flowmapActiveByUrl;

        if (active === 'true') {
            setShowFlowmap(true);
            const params = new URLSearchParams(searchParams.toString());
            params.set('flowmap', 'true');
            const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
            window.history.replaceState({}, '', newUrl);

            setZoomOnceOnData(true);
        } else {
            setShowFlowmap(false);
            const params = new URLSearchParams(searchParams.toString());
            const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, []);
    useEffect(() => {
        if (!map) return;
        if (!filteredFlowmapData?.data) return;

        if (!initialFilteredLoaded.current) {
            const bounds = calculateFlowmapBounds(filteredFlowmapData.data);
            if (bounds) {
                setTimeout(() => {
                    map.fitBounds(bounds, {
                        padding: 50,
                        duration: 1000,
                    });
                }, 150);
            }

            hasFlowmapBeenShown.current = true;

            initialFilteredLoaded.current = true;
        }
    }, [filteredFlowmapData, map, zoomOnceOnData, data]);

    useEffect(() => {
        if (!searchParams) return;

        // searchParams är nu laddade i CSR och du kan läsa filtren
        setUrlFiltersReady(true);
    }, [searchParams]);

    const waitForMap = async () => {
        const MAX_RETRIES = 200;
        return new Promise((resolve, reject) => {
            let retries = 0;
            const checkMapReady = () => {
                retries++;
                if (retries > MAX_RETRIES) {
                    reject(new Error('waitForMap timed out'));
                    return;
                }

                if (!map) {
                    setTimeout(
                        checkMapReady,
                        FLOWMAP_CONFIG.MAP_CHECK_INTERVAL
                    );
                    return;
                }

                if (!map.loaded() || !map.isStyleLoaded() || !map.getStyle()) {
                    setTimeout(
                        checkMapReady,
                        FLOWMAP_CONFIG.MAP_CHECK_INTERVAL
                    );
                    return;
                }

                if (!map.getStyle().layers) {
                    setTimeout(
                        checkMapReady,
                        FLOWMAP_CONFIG.MAP_CHECK_INTERVAL
                    );
                    return;
                }

                resolve();
            };

            checkMapReady();
        });
    };

    useEffect(() => {
        if (!map || !showFlowmap) return;
        if (!filteredFlowmapData) return;

        const updateMapData = async () => {
            try {
                await waitForMap();

                // Verify map is still ready and data is still valid
                if (!map.isStyleLoaded() || !filteredFlowmapData?.data) {
                    return;
                }

                const success = updateFlowmapData(
                    map,
                    filteredFlowmapData.data || [],
                    map.getZoom()
                );

                if (!success) {
                    return;
                }

                if (isInitialLoad.current) {
                    isInitialLoad.current = false;
                }

                if (showFlowmap && !hasFlowmapBeenShown.current) {
                    hasFlowmapBeenShown.current = true;
                }
            } catch (error) {
                console.error('Error updating flowmap source:', error);
            }
        };

        updateMapData();

        // Set up zoom event listener to update arrows
        const handleZoomEnd = () => {
            if (!filteredFlowmapData?.data || !map.isStyleLoaded()) return;

            updateFlowmapData(
                map,
                filteredFlowmapData.data || [],
                map.getZoom()
            );
        };

        map.on('zoomend', handleZoomEnd);

        return () => {
            map.off('zoomend', handleZoomEnd);
        };
    }, [filteredFlowmapData, map, showFlowmap]);

    useEffect(() => {
        if (!map || !showFlowmap || !hasFlowmapBeenShown.current) return;
        if (!filteredFlowmapData?.data || filteredFlowmapData.data.length === 0)
            return;

        const previousData = previousFilteredDataRef.current;
        const currentData = filteredFlowmapData.data;

        const getItemKey = (item) =>
            item.id ||
            `${item.startLong}-${item.startLat}-${item.endLong}-${item.endLat}`;

        // Check if data changed via length + first/last item comparison
        const dataChanged =
            previousData &&
            (previousData.length !== currentData.length ||
                getItemKey(previousData[0]) !== getItemKey(currentData[0]) ||
                getItemKey(previousData[previousData.length - 1]) !==
                    getItemKey(currentData[currentData.length - 1]));

        if (dataChanged) {
            const bounds = calculateFlowmapBounds(currentData);
            if (bounds) {
                const padding = 50;
                map.fitBounds(bounds, {
                    padding: {
                        top: padding,
                        bottom: padding,
                        left: padding,
                        right: padding,
                    },
                    duration: 1000,
                });
            }
        }

        previousFilteredDataRef.current = currentData;
    }, [filteredFlowmapData, map, showFlowmap]);

    useEffect(() => {
        if (!map) return;

        const setupFlowmap = async () => {
            if (!showFlowmap) {
                removeFlowmapLayers(map);
                return;
            }

            // This effect only handles layer setup/cleanup - data updates handled by main effect
        };

        setupFlowmap();

        return () => {
            if (map) {
                removeFlowmapLayers(map);
            }
        };
    }, [map, showFlowmap]);

    useEffect(() => {
        if (!showFlowmapLayer && !showFlowmap) return;
        if (flowmapData) return;

        let isCancelled = false;
        const MAX_PAGES = 100;
        const PAGE_SIZE = 500;

        const fetchFlowmapData = async () => {
            setIsLoadingFlowmap(true);
            setFlowmapError(null);

            try {
                let page = 1;
                const allItems = [];

                while (!isCancelled && page <= MAX_PAGES) {
                    const chunk = await getFlowmapChunk(page, PAGE_SIZE);
                    if (isCancelled) break;
                    if (!chunk.items || chunk.items.length === 0) break;
                    allItems.push(...chunk.items);
                    if (!chunk.hasMore) break;
                    page++;
                    await new Promise((r) => setTimeout(r, 100));
                }

                if (isCancelled) return;

                applyFlowmapColors(allItems);
                const filters = buildFlowmapFilters(allItems);
                setFlowmapData({ filters, data: allItems });
            } catch (error) {
                console.error('Error fetching flowmap data:', error);
                if (!isCancelled) {
                    setFlowmapError(error.message);
                    setFlowmapData({
                        filters: {
                            from_countries: [],
                            to_countries: [],
                            sources: [],
                            types: [],
                            units: [],
                            hs_codes: [],
                            years: [],
                        },
                        data: [],
                    });
                }
            } finally {
                if (!isCancelled) setIsLoadingFlowmap(false);
            }
        };

        fetchFlowmapData();

        return () => {
            isCancelled = true;
        };
    }, [showFlowmapLayer, showFlowmap, flowmapData]);

    // Set up hover and click event handlers for flowmap layers
    useEffect(() => {
        if (!map || !showFlowmap || !filteredFlowmapData?.data?.length) return;

        const setupInteractionHandlers = async () => {
            await waitForMap();

            if (
                !map.getLayer(LAYER_IDS.FLOWMAP) ||
                !map.getLayer(LAYER_IDS.ARROWS)
            ) {
                return;
            }

            const hasClickableLayers =
                map.getLayer(LAYER_IDS.FLOWMAP_EXTRA_CLICKAREA) &&
                map.getLayer(LAYER_IDS.ARROWS_CLICKABLE);
            const hasExtraHoverArea = map.getLayer(
                LAYER_IDS.FLOWMAP_EXTRA_HOVERAREA
            );

            const handleMouseEnter = (e) => {
                // Only show hover popup if no persistent popup exists
                if (persistentPopup.current) {
                    return;
                }

                if (currentPopup.current) {
                    currentPopup.current.remove();
                    currentPopup.current = null;
                }

                map.getCanvas().style.cursor = 'pointer';

                const coordinates = e.lngLat;
                const properties = e.features[0].properties;

                currentPopup.current = createFlowmapPopup(
                    map,
                    coordinates,
                    properties,
                    { persistent: false }
                );
            };

            const handleMouseLeave = () => {
                map.getCanvas().style.cursor = '';

                // Only remove hover popup, not persistent popup
                if (currentPopup.current && !currentPopup.current._persistent) {
                    currentPopup.current.remove();
                    currentPopup.current = null;
                }
            };

            const handleClick = (e) => {
                e.preventDefault();
                if (e.originalEvent) {
                    e.originalEvent.stopPropagation();
                    e.originalEvent.stopImmediatePropagation();
                }

                if (currentPopup.current) {
                    currentPopup.current.remove();
                    currentPopup.current = null;
                }
                if (persistentPopup.current) {
                    persistentPopup.current.remove();
                    persistentPopup.current = null;
                }

                const coordinates = e.lngLat;
                const properties = e.features[0].properties;

                persistentPopup.current = createFlowmapPopup(
                    map,
                    coordinates,
                    properties,
                    { persistent: true }
                );
            };

            const handleMapClick = (e) => {
                const features = map.queryRenderedFeatures(e.point, {
                    layers: [
                        LAYER_IDS.FLOWMAP,
                        LAYER_IDS.ARROWS,
                        LAYER_IDS.FLOWMAP_EXTRA_CLICKAREA,
                        LAYER_IDS.ARROWS_CLICKABLE,
                    ],
                });

                if (features.length > 0) {
                    return;
                }

                if (persistentPopup.current) {
                    persistentPopup.current.remove();
                    persistentPopup.current = null;
                }
            };

            // Set up hover handlers for visible layers
            map.on('mouseenter', LAYER_IDS.ARROWS, handleMouseEnter);
            map.on('mouseleave', LAYER_IDS.ARROWS, handleMouseLeave);

            if (hasExtraHoverArea) {
                map.on(
                    'mouseenter',
                    LAYER_IDS.FLOWMAP_EXTRA_HOVERAREA,
                    handleMouseEnter
                );
                map.on(
                    'mouseleave',
                    LAYER_IDS.FLOWMAP_EXTRA_HOVERAREA,
                    handleMouseLeave
                );
            } else {
                map.on('mouseenter', LAYER_IDS.FLOWMAP, handleMouseEnter);
                map.on('mouseleave', LAYER_IDS.FLOWMAP, handleMouseLeave);
            }

            // Set up click handlers - use clickable layers if available, otherwise use visible layers
            if (hasClickableLayers) {
                map.on('click', LAYER_IDS.FLOWMAP_EXTRA_CLICKAREA, handleClick);
                map.on('click', LAYER_IDS.ARROWS_CLICKABLE, handleClick);
            } else {
                map.on('click', LAYER_IDS.FLOWMAP, handleClick);
                map.on('click', LAYER_IDS.ARROWS, handleClick);
            }

            map.on('click', handleMapClick);

            return () => {
                // Remove hover handlers

                if (hasExtraHoverArea) {
                    map.off(
                        'mouseenter',
                        LAYER_IDS.FLOWMAP_EXTRA_HOVERAREA,
                        handleMouseEnter
                    );
                    map.off(
                        'mouseleave',
                        LAYER_IDS.FLOWMAP_EXTRA_HOVERAREA,
                        handleMouseLeave
                    );
                } else {
                    map.off('mouseenter', LAYER_IDS.FLOWMAP, handleMouseEnter);
                    map.off('mouseleave', LAYER_IDS.FLOWMAP, handleMouseLeave);
                }

                map.off('mouseenter', LAYER_IDS.ARROWS, handleMouseEnter);
                map.off('mouseleave', LAYER_IDS.ARROWS, handleMouseLeave);

                // Remove click handlers
                if (hasClickableLayers) {
                    map.off(
                        'click',
                        LAYER_IDS.FLOWMAP_EXTRA_CLICKAREA,
                        handleClick
                    );
                    map.off('click', LAYER_IDS.ARROWS_CLICKABLE, handleClick);
                } else {
                    map.off('click', LAYER_IDS.FLOWMAP, handleClick);
                    map.off('click', LAYER_IDS.ARROWS, handleClick);
                }
                map.off('click', handleMapClick);

                // Clean up popups
                if (currentPopup.current) {
                    currentPopup.current.remove();
                    currentPopup.current = null;
                }
                if (persistentPopup.current) {
                    persistentPopup.current.remove();
                    persistentPopup.current = null;
                }
            };
        };

        let isCancelled = false;
        let cleanup;
        setupInteractionHandlers().then((cleanupFn) => {
            if (isCancelled) {
                cleanupFn?.();
            } else {
                cleanup = cleanupFn;
            }
        });

        return () => {
            isCancelled = true;
            if (cleanup) cleanup();
            if (currentPopup.current) {
                currentPopup.current.remove();
                currentPopup.current = null;
            }
            if (persistentPopup.current) {
                persistentPopup.current.remove();
                persistentPopup.current = null;
            }
        };
    }, [map, showFlowmap, filteredFlowmapData]);

    const contextValue = useMemo(
        () => ({
            flowmapData,
            filteredFlowmapData,
            setFlowmapData,
            setFilteredFlowmapData,
            showFlowmap,
            setShowFlowmap,
            activeFilterCount,
            setActiveFilterCount,
            resetFilters: resetFiltersFn,
            setResetFilters,
            isLoadingFlowmap,
            flowmapError,
        }),
        [
            flowmapData,
            filteredFlowmapData,
            showFlowmap,
            activeFilterCount,
            resetFiltersFn,
            isLoadingFlowmap,
            flowmapError,
        ]
    );

    return (
        <FlowmapContext.Provider value={contextValue}>
            {children}
        </FlowmapContext.Provider>
    );
};

export const useFlowmap = () => {
    const context = useContext(FlowmapContext);
    if (!context) {
        throw new Error('useFlowmap must be used within a FlowmapProvider');
    }
    return context;
};

export default FlowmapProvider;
